import fetchItemPolicyEvaluations from 'actions/businessTraveller/fetchItemPolicyEvaluations'
import type { FetchItemPolicyBudgetParams } from 'businessTraveller/types/itemPolicyEvaluation.types'
import { normalizeFlightIataAirportCodeForItemPolicy } from 'businessTraveller/utils/normalizeFlightIataAirportCodeForItemPolicy'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { useEffect } from 'react'

type Params = FetchItemPolicyBudgetParams & {
  enabled?: boolean
}

// The budget bar is a single budget-only line: an item with no `cost`, so evaluate returns the budget
// caps + presenter blocks (credit fields come back null and are unused here).
function buildBudgetItem(params: FetchItemPolicyBudgetParams): App.BusinessTraveller.ItemPolicyEvaluationItem {
  if (params.itemPolicyType === 'HOTEL') {
    // Location is exactly one of place-id or coordinates; prefer the place id when present.
    const location = params.lePlaceId
      ? { lePlaceId: params.lePlaceId }
      : { latitude: params.latitude ?? '', longitude: params.longitude ?? '' }
    return {
      itemPolicyType: 'HOTEL',
      key: params.cacheKey,
      currencyCode: params.currencyCode,
      numberOfNights: params.numberOfNights,
      roomCount: params.roomCount,
      location,
    }
  }

  if (params.itemPolicyType === 'FLIGHT') {
    return {
      itemPolicyType: 'FLIGHT',
      key: params.cacheKey,
      currencyCode: params.currencyCode,
      departingAirportCode:
        normalizeFlightIataAirportCodeForItemPolicy(params.departingAirportCode) ?? params.departingAirportCode,
      arrivingAirportCode:
        normalizeFlightIataAirportCodeForItemPolicy(params.arrivingAirportCode) ?? params.arrivingAirportCode,
      flightViewType: params.flightViewType,
      travellerCount: params.travellerCount,
    }
  }

  return {
    itemPolicyType: 'CAR_HIRE',
    key: params.cacheKey,
    currencyCode: params.currencyCode,
    numberOfDays: params.numberOfDays,
    travellerCount: params.travellerCount,
  }
}

function hasValidBudgetQueryContext(params: FetchItemPolicyBudgetParams): boolean {
  return params.itemPolicyType === 'CAR_HIRE'
    ? params.numberOfDays >= 1 && params.travellerCount >= 1
    : params.itemPolicyType === 'FLIGHT'
      ? !!(params.departingAirportCode && params.arrivingAirportCode)
      : !!(params.lePlaceId || (params.latitude && params.longitude))
}

/** Fetches and returns the item policy budget evaluation for a hotel, flight, or car hire item. */
function useFetchItemPolicyBudget(
  params: Params,
): App.BusinessTraveller.StatefulData<App.BusinessTraveller.ItemPolicyEvaluationResult> | undefined {
  const { enabled = true, ...fetchParams } = params
  const { businessId, cacheKey, employeeIds } = fetchParams
  const appDispatch = useAppDispatch()
  const evaluation = useAppSelector(state => state.businessTraveller.itemPolicyEvaluations?.[cacheKey])
  const employeeIdsKey = employeeIds.join(',')
  const isEnabled = enabled && hasValidBudgetQueryContext(fetchParams)

  useEffect(() => {
    if (!isEnabled || !businessId || !cacheKey || employeeIds.length === 0) {
      return
    }
    appDispatch(fetchItemPolicyEvaluations([cacheKey, buildBudgetItem(fetchParams), employeeIds], businessId))
    // cacheKey encodes semantic request identity; callers regenerate it when any input changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- avoid re-firing on new `fetchParams` object identity each render
  }, [appDispatch, isEnabled, businessId, cacheKey, employeeIdsKey])

  return evaluation
}

export default useFetchItemPolicyBudget
